import { ArrowRight2, Messages1 } from 'iconsax-react';
import { sortBy, take } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams, useTypedState } from 'react-router-typesafe-routes';
import FriendInfoBackgroundImage from '@/assets/images/backgrounds/friend-info.png?format=webp&w=600&url';
import Alert from '@/assets/images/icons/toast/alert.svg?react';
import AppCell from '@/components/common/app-cell';
import CopyButton from '@/components/common/copy-button';
import TruncateCheck from '@/components/common/truncate-check';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import CommonGroupBlock from '@/components/user/common-group-block';
import FriendUserHero from '@/components/user/friend-user-hero';
import UserHeaderMenu from '@/components/user/user-header-menu';
import { useBrand } from '@/hooks/pwa/use-brand';
import useCreateSession from '@/hooks/sessions/use-create-session';
import { useTagsByUserId } from '@/hooks/tags/use-tags';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { useBlacklisted } from '@/hooks/users/use-blacklist';
import useFriendInfo from '@/hooks/users/use-friend-info';
import ROUTES from '@/routes';

export default function UserByIdRoute() {
  const { t } = useTranslation();
  const brand = useBrand();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { userId: friendId } = useTypedParams(ROUTES.CONTACT.USERS.BY_ID);
  const historyState = useTypedState(ROUTES.CONTACT.USERS.BY_ID);
  const { tags } = useTagsByUserId(friendId);
  const { friendInfo, isLoading } = useFriendInfo(friendId);
  const { sessionId, mutateAsync: createSession } = useCreateSession(friendId);
  const { data: isBlacklisted } = useBlacklisted(friendId);

  async function handleGoToSession() {
    if (sessionId) {
      return navigate(
        ROUTES.MESSAGES.SESSIONS.$buildPath({
          params: {
            id: sessionId,
          },
        }),
      );
    }

    const result = await createSession(friendId);
    if (result.Data?.ID) {
      return navigate(
        ROUTES.MESSAGES.SESSIONS.$buildPath({
          params: {
            id: result.Data?.ID,
          },
        }),
      );
    }
  }

  const commonGroups = useMemo(
    () =>
      take(
        sortBy(friendInfo?.CommonGroup ?? [], (a) => -a.LastMsgTimeStamp),
        3,
      ),
    [friendInfo?.CommonGroup],
  );

  return (
    <PageLayout
      title={isLoading ? t('user.title') : friendInfo?.FriendInfo.FriendNickName}
      className='pb-safe-offset-4'
      headerConfig={{
        navigateBack: !isDesktop,
        headerLabel: !isLoading && friendInfo?.IsFriend ? t('user.title') : t('user.title-default'),
        className: 'bg-transparent z-40',
        actions: friendInfo?.IsFriend && (
          <UserHeaderMenu
            userId={friendId}
            navigateBack={historyState.from}
          />
        ),
      }}>
      <div
        style={{
          backgroundImage: `url(${FriendInfoBackgroundImage})`,
        }}
        className='bg-cover pointer-events-none bg-center bg-no-repeat h-20vh z-0 absolute inset-x-0 top-0'>
        <div className='absolute inset-0 pointer-events-none bg-primary mix-blend-color desktop:rounded' />
      </div>

      <LoadingLayout promise={isLoading}>
        {() =>
          friendInfo && (
            <div className='z-20 flex flex-1 gap-2 flex-col desktop:mb-safe-or-6  mt-10 overflow-visible basis-50dvh rounded-t-2xl bg-neutral-0'>
              <FriendUserHero
                avatarUrl={friendInfo.FriendInfo.FriendHeadImg}
                name={friendInfo.FriendInfo.FriendNickName}
                className='-mt-2'
              />

              <hr className='my-1 mx-5' />

              <div className='flex flex-col px-5'>
                {friendInfo.IsFriend && (
                  <>
                    <AppCell
                      label={t('user.remark.label')}
                      className='px-0'
                      onClick={() => navigate(ROUTES.CONTACT.USERS.BY_ID.$.REMARK.$path({ relative: true }))}
                      chevron>
                      <p className='max-w-60vw overflow-hidden desktop:max-w-60 text-nowrap text-ellipsis break-all text-h2 mode-embedded:text-body1 desktop:text-sm text-secondary'>
                        {friendInfo.FriendInfo.RemarkName}
                      </p>
                    </AppCell>

                    <AppCell
                      className='px-0 py-1'
                      label={t('user.chat-id', { brandShortName: brand.shortName })}
                      content={
                        friendInfo.FriendInfo.VChatID && (
                          <>
                            <span className='font-mono flex items-center gap-2 text-secondary'>
                              {friendInfo.FriendInfo.VChatID}
                            </span>
                            <CopyButton content={friendInfo.FriendInfo.VChatID} />
                          </>
                        )
                      }
                    />
                    {!isDesktop && (
                      <AppCell
                        className='px-0'
                        label={t('common.tag')}
                        content={
                          <TruncateCheck
                            suffix={(tags.length > 2 && ', …') || ''}
                            forceTruncate={tags.length > 2}>
                            {(tags.length > 2 ? take(tags, 2) : tags).map((tag) => tag.TagName).join(', ')}
                          </TruncateCheck>
                        }
                        onClick={() => navigate(ROUTES.CONTACT.USERS.BY_ID.$.TAGS.$path({ relative: true }))}
                        chevron
                      />
                    )}
                  </>
                )}
              </div>
              {isBlacklisted ? (
                <div className='flex items-center gap-2 text-body1 w-full justify-center mt-10'>
                  <Alert className='size-6' /> {t('blacklist.add-success')}
                </div>
              ) : (
                <>
                  <hr className='my-2 h-2 bg-secondary' />
                  <div className='flex flex-1 flex-col gap-2 px-5'>
                    <div className='border-surface text-sm mt-0 pt-2'>
                      <div className='flex items-center justify-between min-h-8'>
                        <h5 className='font-bold'>{`${t('user.same-group')} (${
                          friendInfo.CommonGroup?.length ?? 0
                        })`}</h5>

                        {(friendInfo.CommonGroup?.length ?? 0) > 3 && (
                          <button
                            type='button'
                            className='btn btn-ghost btn-sm px-0'
                            onClick={() => navigate(ROUTES.CONTACT.USERS.BY_ID.$.GROUPS.$path({ relative: true }))}>
                            {t('common.view-all')}
                            <ArrowRight2 className='size-5' />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className='flex flex-1 flex-col items-stretch min-h-48'>
                      <CommonGroupBlock groups={commonGroups} />
                    </div>
                  </div>

                  <div className='mb-2 flex items-end justify-stretch px-5'>
                    {friendInfo.IsFriend && (
                      <button
                        type='button'
                        className='btn btn-primary gap-1 min-h-10 h-10 items-center w-full'
                        onClick={handleGoToSession}>
                        <Messages1 className='size-5' />

                        <span className='text-body1'>{t('user.chat')}</span>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )
        }
      </LoadingLayout>
    </PageLayout>
  );
}
